import { routeNames } from 'src/modules/core/constants/routeNames'
import type { RequirementsAction, ResolveRequirementsInput } from './types'

export function resolveRequirements(input: ResolveRequirementsInput): RequirementsAction {
  const {
    path,
    restricted,
    isAuthenticated,
    profile,
    bookingOnboarding,
    bookingOnboardingLoaded,
    pendingConnectedAppPath,
  } = input

  if (path === routeNames.forceLogout.path) {
    return { type: 'none' }
  }

  // Login
  if (!isAuthenticated && restricted) {
    return { type: 'redirect', to: routeNames.login.path }
  }

  // Booking Onboarding
  if (
    isAuthenticated &&
    profile?.is_booking_user &&
    bookingOnboardingLoaded &&
    !bookingOnboarding?.onboarding_completed
  ) {
    if (path !== routeNames.bookingOnboarding.path) {
      return { type: 'redirect', to: routeNames.bookingOnboarding.path }
    }
  }

  // Regular Onboarding (including connected-app authorize — same rule as dashboard)
  if (
    isAuthenticated &&
    !profile?.is_booking_user &&
    !profile?.completed_onboarding &&
    isRouteWithCompletedOnboarding(path)
  ) {
    return { type: 'redirect', to: routeNames.configuration.path }
  }

  // Sync Offline Changes
  if (isAuthenticated && !profile?.refresh_token && isRestrictedAppArea(path)) {
    return { type: 'render', component: 'syncOfflineChangesModal' }
  }

  // Connected App Authorize (defer until booking onboarding is finished — same bar as approve UI)
  if (isAuthenticated && pendingConnectedAppPath && path !== routeNames.connectedAppAuthorize.path) {
    const mustFinishBookingOnboarding =
      profile?.is_booking_user && bookingOnboardingLoaded && !bookingOnboarding?.onboarding_completed
    if (!mustFinishBookingOnboarding) {
      return { type: 'redirect', to: pendingConnectedAppPath }
    }
  }

  // Dashboard
  if (isAuthenticated && profile?.completed_onboarding && isOnboardingRoute(path)) {
    return { type: 'redirect', to: routeNames.dashboard.path }
  }

  // Billing surfaces hidden: the subscription screens have nothing to show and
  // no charge to take. The routes stay registered so they come back with paid
  // plans; only the way in is closed.
  if (isAuthenticated && profile?.hide_billing_ui && isSubscriptionRoute(path)) {
    return { type: 'redirect', to: routeNames.dashboard.path }
  }

  // None
  return { type: 'none' }
}

function isRouteWithCompletedOnboarding(path: string): boolean {
  return path.includes('dashboard') || path.includes('subscription')
}

function isOnboardingRoute(path: string): boolean {
  return (
    path.includes('onboarding') &&
    path !== routeNames.login.path &&
    path !== routeNames.bookingOnboarding.path &&
    path !== routeNames.connectedAppAuthorize.path &&
    // The handoff redeem page. Without this the router bounces it to the
    // dashboard the instant it authenticates, silently dropping `next`.
    path !== routeNames.connectedAppLogin.path &&
    path !== routeNames.syncCalendar.path &&
    path !== routeNames.forceLogout.path
  )
}

function isSubscriptionRoute(path: string): boolean {
  return path.startsWith('/subscription')
}

function isRestrictedAppArea(path: string): boolean {
  return path.startsWith(routeNames.dashboardPrefix.path)
}
